import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DecoratorNode,
  $getNodeByKey,
  $getRoot,
  $getSelection,
  $isRangeSelection,
} from 'lexical';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $isHeadingNode } from '@lexical/rich-text';
import { $isHeadingAnchorNode } from './HeadingAnchorNode';
import {
  getMetricsForProfile,
  getUsablePageHeight,
} from '../pagination/reportPageMetrics';

const OFFICIAL_TOC_MAX_ROWS = 30;

const resolveOverflowLabel = (locale) => (
  String(locale || '').toLowerCase().startsWith('it')
    ? 'Ulteriori voci presenti nel corpo del documento'
    : 'Additional entries are available in the document body'
);

const resolveMetricsForRoot = (rootElement) => {
  if (typeof document === 'undefined') {
    return getMetricsForProfile('screen');
  }
  const canvasElement = rootElement?.closest?.('.report-paged-canvas');
  const profile = canvasElement?.dataset?.reportPaginationProfile
    || document.body?.dataset?.reportPaginationProfile
    || 'screen';
  return getMetricsForProfile(profile);
};

const getHeadingLevel = (tag) => {
  const level = Number(tag?.replace('h', ''));
  if (Number.isNaN(level)) {
    return 1;
  }
  return Math.min(Math.max(level, 1), 3);
};

const getEstimatedPage = (editor, key) => {
  if (typeof window === 'undefined') {
    return null;
  }
  const rootElement = editor.getRootElement();
  const headingElement = editor.getElementByKey(key);
  if (!rootElement || !headingElement) {
    return null;
  }
  const hardPageValue = Number(headingElement.dataset?.reportPage);
  if (Number.isFinite(hardPageValue) && hardPageValue > 0) {
    return hardPageValue;
  }
  const rootRect = rootElement.getBoundingClientRect();
  const headingRect = headingElement.getBoundingClientRect();
  const topOffset = headingRect.top - rootRect.top;
  const metrics = resolveMetricsForRoot(rootElement);
  const usablePageHeight = getUsablePageHeight(metrics);
  const normalizedOffset = Math.max(0, topOffset + metrics.contentTopInsetPx);
  return Math.max(1, Math.floor(normalizedOffset / usablePageHeight) + 1);
};

const isScrollableElement = (element) => {
  if (!element || typeof window === 'undefined') {
    return false;
  }
  const style = window.getComputedStyle(element);
  const overflowY = style?.overflowY || '';
  return /(auto|scroll|overlay)/.test(overflowY) && element.scrollHeight > element.clientHeight + 1;
};

const getScrollContainers = (element) => {
  if (!element || typeof window === 'undefined') {
    return [window];
  }
  const containers = [];
  let parent = element.parentElement;
  while (parent && parent !== document.body) {
    if (isScrollableElement(parent)) {
      containers.push(parent);
    }
    parent = parent.parentElement;
  }
  containers.push(window);
  return containers;
};

function TableOfContentsComponent() {
  const { t } = useTranslation();
  const [editor] = useLexicalComposerContext();
  const [headings, setHeadings] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const isInteractive = editor.isEditable();
  const tocTitle = t('reports.review.template.sections.toc');
  const blockClassName = isInteractive
    ? 'report-draft-editor__toc-block report-draft-editor__toc-block--interactive'
    : 'report-draft-editor__toc-block report-draft-editor__toc-block--readonly';

  const buildHeadings = useCallback(() => {
    const items = [];
    const counters = [];
    const isOfficialPrintMode = typeof document !== 'undefined'
      && document.body?.dataset?.reportExportProfile === 'official_v1';
    const visit = (node) => {
      if ($isHeadingAnchorNode(node) || $isHeadingNode(node)) {
        const text = node.getTextContent().trim();
        if (text) {
          const level = getHeadingLevel(node.getTag());
          if (isOfficialPrintMode && level > 2) {
            return;
          }
          counters[level - 1] = (counters[level - 1] || 0) + 1;
          counters.length = level;
          items.push({
            id: $isHeadingAnchorNode(node) ? node.getAnchorId() : node.getKey(),
            key: node.getKey(),
            text,
            level,
            number: counters.join('.'),
            page: getEstimatedPage(editor, node.getKey()),
            isNotice: false,
          });
        }
      }
      if (typeof node.getChildren === 'function') {
        node.getChildren().forEach(visit);
      }
    };
    $getRoot().getChildren().forEach(visit);
    if (!isOfficialPrintMode || items.length <= OFFICIAL_TOC_MAX_ROWS) {
      return items;
    }
    const locale = document?.body?.dataset?.reportLocale || 'en';
    const maxRegularRows = Math.max(1, OFFICIAL_TOC_MAX_ROWS - 1);
    return [
      ...items.slice(0, maxRegularRows),
      {
        id: '__toc-overflow-notice',
        key: null,
        text: resolveOverflowLabel(locale),
        level: 2,
        number: '',
        page: '',
        isNotice: true,
      },
    ];
  }, [editor]);

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const items = buildHeadings();
        setHeadings(items);

        const selection = $getSelection();
        if (!$isRangeSelection(selection)) {
          return;
        }

        const anchorNode = selection.anchor.getNode();
        if (anchorNode.getType?.() === 'root') {
          return;
        }
        let topLevel = null;
        try {
          topLevel = anchorNode.getTopLevelElementOrThrow();
        } catch (error) {
          return;
        }
        if ($isHeadingAnchorNode(topLevel)) {
          setActiveId(topLevel.getAnchorId());
          return;
        }

        const siblings = $getRoot().getChildren();
        let lastHeadingId = null;
        for (const sibling of siblings) {
          if ($isHeadingAnchorNode(sibling)) {
            lastHeadingId = sibling.getAnchorId();
          }
          if (sibling.is(topLevel)) {
            break;
          }
        }
        if (lastHeadingId) {
          setActiveId(lastHeadingId);
        }
      });
    });
  }, [editor, buildHeadings]);

  useEffect(() => {
    if (!isInteractive) {
      setActiveId(null);
      return undefined;
    }
    if (headings.length === 0) {
      return undefined;
    }
    if (typeof window === 'undefined') {
      return undefined;
    }

    const rootElement = editor.getRootElement();
    const scrollContainers = getScrollContainers(rootElement);

    const handleScroll = () => {
      let nextActiveId = headings[0]?.id || null;
      headings.forEach((item) => {
        if (!item?.key || item.isNotice) {
          return;
        }
        const element = editor.getElementByKey(item.key);
        if (!element) {
          return;
        }
        const offset = element.getBoundingClientRect().top - 140;
        if (offset <= 0) {
          nextActiveId = item.id;
        }
      });
      setActiveId(prevActiveId => (nextActiveId && nextActiveId !== prevActiveId ? nextActiveId : prevActiveId));
      setHeadings((prevHeadings) => {
        let changed = false;
        const withPages = prevHeadings.map((item) => {
          if (!item?.key || item.isNotice) {
            return item;
          }
          const nextPage = getEstimatedPage(editor, item.key);
          if (item.page === nextPage) {
            return item;
          }
          changed = true;
          return {
            ...item,
            page: nextPage,
          };
        });
        return changed ? withPages : prevHeadings;
      });
    };

    scrollContainers.forEach((container) => {
      container.addEventListener('scroll', handleScroll, { passive: true });
    });
    window.addEventListener('resize', handleScroll);
    let mutationObserver = null;
    if (rootElement && typeof MutationObserver !== 'undefined') {
      mutationObserver = new MutationObserver(handleScroll);
      mutationObserver.observe(rootElement, {
        subtree: true,
        attributes: true,
        attributeFilter: ['data-report-page', 'style'],
      });
    }
    handleScroll();
    return () => {
      scrollContainers.forEach((container) => {
        container.removeEventListener('scroll', handleScroll);
      });
      window.removeEventListener('resize', handleScroll);
      if (mutationObserver) {
        mutationObserver.disconnect();
      }
    };
  }, [editor, headings, isInteractive]);

  const handleJump = useCallback(
    (item) => {
      if (!isInteractive) {
        return;
      }
      const element = editor.getElementByKey(item.key);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      editor.update(() => {
        const node = $getNodeByKey(item.key);
        if (node && $isHeadingNode(node)) {
          node.select();
        }
      });
    },
    [editor, isInteractive]
  );

  const normalizeItemText = useCallback(
    (text) => {
      if (typeof text !== 'string') {
        return '';
      }
      const trimmed = text.trim();
      if (trimmed.toLowerCase() === tocTitle.trim().toLowerCase()) {
        return tocTitle;
      }
      return text;
    },
    [tocTitle]
  );

  const content = useMemo(() => {
    if (headings.length === 0) {
      return (
        <div className="report-draft-editor__toc-empty">
          {t('reports.review.editor.tocEmpty')}
        </div>
      );
    }
    return (
      <ul className="report-draft-editor__toc-list">
        {headings.map((item) => (
          <li
            key={item.id}
            className={`report-draft-editor__toc-item report-draft-editor__toc-item--level-${item.level}${
              item.isNotice ? ' report-draft-editor__toc-item--notice' : ''
            }${
              isInteractive && activeId === item.id && !item.isNotice ? ' report-draft-editor__toc-item--active' : ''
            }`}
          >
            {item.isNotice ? (
              <div className="report-draft-editor__toc-notice" aria-hidden="true">
                <span className="report-draft-editor__toc-number" />
                <span className="report-draft-editor__toc-title">{normalizeItemText(item.text)}</span>
                <span className="report-draft-editor__toc-leader" aria-hidden="true" />
                <span className="report-draft-editor__toc-page" aria-hidden="true" />
              </div>
            ) : (
              <button
                type="button"
                onClick={() => handleJump(item)}
                disabled={!isInteractive}
                aria-current={isInteractive && activeId === item.id ? 'true' : undefined}
              >
                <span className="report-draft-editor__toc-number">{item.number}</span>
                <span className="report-draft-editor__toc-title">{normalizeItemText(item.text)}</span>
                <span className="report-draft-editor__toc-leader" aria-hidden="true" />
                <span className="report-draft-editor__toc-page" aria-hidden="true">
                  {item.page || ''}
                </span>
              </button>
            )}
          </li>
        ))}
      </ul>
    );
  }, [headings, activeId, handleJump, isInteractive, normalizeItemText]);

  return (
    <div className={blockClassName} contentEditable={false}>
      {content}
    </div>
  );
}

export class TableOfContentsNode extends DecoratorNode {
  static getType() {
    return 'table-of-contents';
  }

  static clone(node) {
    return new TableOfContentsNode(node.__key);
  }

  createDOM() {
    const dom = document.createElement('div');
    dom.className = 'report-draft-editor__toc';
    return dom;
  }

  updateDOM() {
    return false;
  }

  decorate() {
    return <TableOfContentsComponent />;
  }

  isIsolated() {
    return true;
  }

  isInline() {
    return false;
  }

  exportJSON() {
    return {
      type: 'table-of-contents',
      version: 1,
    };
  }

  static importJSON() {
    return $createTableOfContentsNode();
  }
}

export function $createTableOfContentsNode() {
  return new TableOfContentsNode();
}

export function $isTableOfContentsNode(node) {
  return node instanceof TableOfContentsNode;
}
